
import { loadSettingsFromDatabase, saveSettingsToDatabase } from './services/settings-service';
import { STORAGE_KEYS } from './constants/storage-keys';

// Settings interface
export interface WellnessSettings {
  calorieGoal: number;
  walkingGoal: number;  // in minutes
  fastingGoal: number;  // in milliseconds
  sleepGoal: number;    // in hours
  profileId?: string;   // for user profiles
  waterGoal: number;    // in ml
  waterUnit: 'ml' | 'oz';
  proteinPct: number;   // % of calorie goal from protein
  carbPct: number;      // % of calorie goal from carbs
  fatPct: number;       // % of calorie goal from fat
  // Add any new settings here
}

// Default settings
export const DEFAULT_SETTINGS: WellnessSettings = {
  calorieGoal: 2000,
  walkingGoal: 30,      // 30 minutes per day
  fastingGoal: 16 * 60 * 60 * 1000, // 16 hours in milliseconds
  sleepGoal: 8.0,       // 8 hours
  waterGoal: 2000,      // 2000 ml (2 liters) per day
  waterUnit: 'ml',
  proteinPct: 30,
  carbPct: 40,
  fatPct: 30
};

// Load settings
export const loadSettings = (): WellnessSettings => {
  try {
    // For synchronous access, we still use localStorage first
    const settingsStr = localStorage.getItem(STORAGE_KEYS.SETTINGS);
    if (!settingsStr) return DEFAULT_SETTINGS;
    
    const settings = JSON.parse(settingsStr);
    return {
      ...DEFAULT_SETTINGS,
      ...settings,
    };
  } catch (error) {
    console.error('Error loading settings:', error);
    return DEFAULT_SETTINGS;
  }
};

// Save settings
export const saveSettings = (settings: Partial<WellnessSettings>): void => {
  try {
    const currentSettings = loadSettings();
    const newSettings = {
      ...currentSettings,
      ...settings,
    };
    
    // Save to localStorage immediately for responsiveness
    localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(newSettings));
    
    // Then try to save to database (async)
    saveSettingsToDatabase(newSettings)
      .catch(error => {
        console.error('Error saving settings to database:', error);
      });
  } catch (error) {
    console.error('Error saving settings:', error);
  }
};

// Add an async version for components that need to wait for database load
export const loadSettingsAsync = async (): Promise<WellnessSettings> => {
  try {
    return await loadSettingsFromDatabase();
  } catch (error) {
    console.error('Error loading settings async:', error);
    return loadSettings(); // Fall back to sync version
  }
};
